import { BLOCK_TYPES } from '@/components/ui/richEditor/constants.richEditor';
import {
  deleteColumn,
  deleteRow,
  deleteTable,
  insertColumn,
  insertRow,
  insertTable,
  isTableActive
} from '@/components/ui/richEditor/tables/table.utils.richEditor';
import { useSlate } from 'slate-react';

import { ToolbarButton } from '@/components/ui/richEditor/toolbar/button.toolbar.richEditor';
import {
  isBlockActive,
  isLinkActive,
  isMarkActive,
  isSubjectActive,
  toggleBlock,
  toggleLink,
  toggleMark
} from '@/components/ui/richEditor/utils';
import { TooltipProvider } from '@/shadcn/tooltip';
import {
  Bold,
  Heading1,
  Italic,
  LinkIcon,
  List,
  Redo2,
  Underline,
  Undo2
} from 'lucide-react';
import { Icon } from '@stratumn/atomic';

type ToolbarProps = {
  disabled?: boolean;
};

export const Toolbar = ({ disabled = false }: ToolbarProps) => {
  const editor = useSlate();
  const isDisabled = disabled || isSubjectActive(editor);
  const isInTable = isTableActive(editor);

  return (
    <TooltipProvider>
      <div className="mb-2 flex flex-wrap justify-start gap-4">
        <div className="flex flex-wrap gap-1">
          <ToolbarButton
            disabled={isDisabled}
            onClick={() => toggleMark(editor, 'bold')}
            tooltipContent="Bold"
            isActive={isMarkActive(editor, 'bold')}
          >
            <Bold />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled}
            onClick={() => toggleMark(editor, 'italic')}
            tooltipContent="Italic"
            isActive={isMarkActive(editor, 'italic')}
          >
            <Italic />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled}
            tooltipContent="Underline"
            onClick={() => toggleMark(editor, 'underline')}
            isActive={isMarkActive(editor, 'underline')}
          >
            <Underline />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled || isInTable}
            tooltipContent="Heading 1"
            onClick={() => toggleBlock(editor, BLOCK_TYPES.HEADING1)}
            isActive={isBlockActive(editor, BLOCK_TYPES.HEADING1, 'type')}
          >
            <Heading1 />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled || isInTable}
            tooltipContent="Unordered list"
            onClick={() => toggleBlock(editor, BLOCK_TYPES.UNORDERED_LIST)}
            isActive={isBlockActive(editor, BLOCK_TYPES.UNORDERED_LIST, 'type')}
          >
            <List />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled}
            tooltipContent="Add a link"
            onClick={() => toggleLink(editor)}
            isActive={isLinkActive(editor)}
          >
            <LinkIcon />
          </ToolbarButton>

          <ToolbarButton
            disabled={isDisabled || isInTable}
            tooltipContent="Insert a table"
            onClick={() => insertTable(editor)}
          >
            <Icon size={16} name="TableInsert" />
          </ToolbarButton>
        </div>
        {isInTable && (
          <div className="flex flex-wrap gap-1">
            <ToolbarButton
              disabled={isDisabled}
              tooltipContent="Insert a new row"
              onClick={() => insertRow(editor)}
            >
              <Icon size={16} name="RowInsert" />
            </ToolbarButton>
            <ToolbarButton
              disabled={isDisabled}
              tooltipContent="Delete current row"
              onClick={() => deleteRow(editor)}
            >
              <Icon size={16} name="RowDelete" />
            </ToolbarButton>
            <ToolbarButton
              disabled={isDisabled}
              tooltipContent="Insert a new column"
              onClick={() => insertColumn(editor)}
            >
              <Icon size={16} name="ColumnInsert" />
            </ToolbarButton>
            <ToolbarButton
              disabled={isDisabled}
              tooltipContent="Delete current column"
              onClick={() => deleteColumn(editor)}
            >
              <Icon size={16} name="ColumnDelete" />
            </ToolbarButton>
            <ToolbarButton
              disabled={isDisabled}
              tooltipContent="Delete current table"
              onClick={() => deleteTable(editor)}
            >
              <Icon size={16} name="TableDelete" />
            </ToolbarButton>
          </div>
        )}
        <div className="ml-auto flex flex-wrap gap-1">
          <ToolbarButton
            disabled={isDisabled || editor.history.undos.length <= 1}
            tooltipContent="Undo last change"
            onClick={editor.undo}
          >
            <Undo2 />
          </ToolbarButton>
          <ToolbarButton
            disabled={isDisabled || editor.history.redos.length === 0}
            tooltipContent="Redo last change"
            onClick={editor.redo}
          >
            <Redo2 />
          </ToolbarButton>
        </div>
      </div>
    </TooltipProvider>
  );
};
